import axiosClient from './axiosClient';
import { IApiBaseResponse } from '~/utils/interface/common';
import {
  IFinanceActivityLog,
  IBudgetTrackerJobDetailsListResponse,
  ICreditMemoJobDetailsListResponse,
  IDetailsBudgetTrackerJobDetails,
  IDetailsCreditMemoJobDetails,
  IInvoiceDetailsJobDetails,
  IInvoiceTaxRate,
  IInvoiceInLogPaymentModal,
  IInvoiceInViewInvoice,
  IInvoiceJobDetailsListResponse,
  IItemCreateNewBudget,
  IJobDetails,
  IOverviewActivityFeed,
  IOverviewCustomerDetails,
  IOverviewJobDetails,
  IOverviewJobTaskListResponse,
  IOverviewWorkOrderDetails,
  IOverviewWorkOrderListResponse,
  IParamsGetInvoiceJobDetails,
  IParamsGetListProductInvoice,
  IParamsGetOverviewJobTaskList,
  IParamsGetOverviewWorkOrderList,
  IPayloadBudgetTrackerJobDetails,
  IPayloadCreateCreditMemo,
  IPayloadCreateInvoice,
  IPayloadCreateJobTask,
  IPayloadEmailInvoice,
  IPayloadLogPayment,
  IPayloadUpdateOverviewCustomerDetails,
  IPayloadUpdateOverviewJobDetails,
  IPaymentDetails,
  IPaymentJobDetailsListResponse,
  IProductInvoice
} from '~/utils/interface/jobDetails';
import { urlApiJobDetails } from '~/utils/constants/apis';

export interface IJobDocument {
  id: number;
  category: string;
  fileName: string;
  fileSize: string;
  fileType: string;
  fileId: number;
  jobId: number;
  visibility: string;
  description?: string;
  uploadedBy: string;
  createdAt: string;
  file: {
    id: number;
    url: string;
    type: string;
    resolution?: string;
    mimetype: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface IGetJobDocumentsParams {
  page?: number;
  limit?: number;
  textSearch?: string;
  fileType?: string;
  sortBy?: string;
  sortType?: 'ASC' | 'DESC';
}

export interface IGetJobDocumentsResponse {
  responses: IJobDocument[];
  pagination: {
    totalItems: number;
    totalPage: number;
    page: number;
    limit: number;
    skip: number;
  };
}

export interface ICreateJobDocumentPayload {
  file: File;
  category: string;
  fileSize: string;
  fileType: string;
  fileName: string;
  description?: string;
  jobId: number;
  visibility: string;
}

export interface ICreateJobDocumentResponse {
  id: number;
  name: string;
  description?: string;
  size: string;
  uploadedDate: string;
  uploadedBy: string;
  documentUrl: string;
}

export interface IMaterialOrderResponse {
  id: string;
  materialOrderId: string;
  date: string;
  internalNotes: string;
  totalPrice: string;
  status: string;
}

export interface IBranch {
  shipToAccount: string;
  branchNumber: string;
  branchName: string;
}

export interface IProduct {
  itemNumber: string;
  itemDescription: string;
  familyName: string | null;
  familyId: string | null;
  isDimensional: boolean;
  colorName: string;
  category: string;
  status: string;
  variationUom: string;
  variationUomCode: string;
  uom: string;
}

export interface IProductPricingLine {
  product: IProduct;
  quantity: number;
}

export interface IProductPricingPayload {
  shipToAccount: string;
  branchNumber: string;
  purpose: string;
  lines: IProductPricingLine[];
}

export interface IProductPricingResponse {
  product: IProduct;
  quantity: number;
  price: number;
  lineTotal: number;
}

export interface ICreateMaterialOrderLine {
  product: IProduct;
  quantity: number;
  price: number;
}

export interface ICreateMaterialOrderSection {
  sectionName: string;
  lines: ICreateMaterialOrderLine[];
}

export interface ICreateMaterialOrderPayload {
  materialOrderId: string;
  date: string;
  streetAddress: string;
  city: string;
  state: string;
  stateBranch: string;
  zipCode: string;
  requestedDeliveryDate: string;
  notes: string;
  notesToSupplier: string;
  shipToAccount: string;
  deliveryService: string;
  instructionsTypeCode: string;
  fromTime: string;
  toTime: string;
  branchNumber: string;
  sections: ICreateMaterialOrderSection[];
}

export interface IMaterialOrderDetailLine {
  product: IProduct;
  quantity: string;
  price: string;
  lineTotal: number;
}

export interface IMaterialOrderDetailSection {
  lines: IMaterialOrderDetailLine[];
}

export interface IMaterialOrderDetail {
  id: string;
  materialOrderId: string;
  date: string;
  internalNotes: string;
  totalPrice: string;
  status: string;
  branchNumber: number;
  fromTime: string;
  toTime: string;
  streetAddress: string;
  city: string;
  state: string;
  stateBranch: string | null;
  zipCode: string;
  requestedDeliveryDate: string;
  notesToSupplier: string;
  notes: string;
  deliveryService: string;
  instructionsTypeCode: string;
  sections: IMaterialOrderDetailSection[];
  totalCost: string;
}

const jobDetailsApi = {
  getJobDetails(id: string) {
    return axiosClient.get<IApiBaseResponse<IJobDetails>>(`${urlApiJobDetails.getJobDetails(id)}`);
  },

  getOverviewJobDetails(id: string) {
    return axiosClient.get<IApiBaseResponse<IOverviewJobDetails>>(`${urlApiJobDetails.getOverviewJobDetails(id)}`);
  },

  getJobDocuments(id: string, params?: IGetJobDocumentsParams) {
    return axiosClient.get<IApiBaseResponse<IGetJobDocumentsResponse>>(
      `${urlApiJobDetails.getJobDocuments(id)}`,
      { params }
    );
  },

  updateOverviewJobDetails(id: string, data: IPayloadUpdateOverviewJobDetails) {
    return axiosClient.put<IApiBaseResponse<IOverviewJobDetails>>(`${urlApiJobDetails.updateOverviewJobDetails(id)}`, data);
  },

  addAssignees(id: string, data: { accountIds: number[] }) {
    return axiosClient.put<IApiBaseResponse<any>>(`${urlApiJobDetails.addAssignees(id)}`, data);
  },

  getOverviewCustomerDetails(id: string) {
    return axiosClient.get<IApiBaseResponse<IOverviewCustomerDetails>>(`${urlApiJobDetails.getOverviewCustomerDetails(id)}`);
  },

  updateOverviewCustomerDetails(id: string, data: IPayloadUpdateOverviewCustomerDetails) {
    return axiosClient.put<IApiBaseResponse<any>>(`${urlApiJobDetails.updateOverviewCustomerDetails(id)}`, data);
  },

  getOverviewActivityFeed(id: string) {
    return axiosClient.get<IApiBaseResponse<IOverviewActivityFeed[]>>(`${urlApiJobDetails.getOverviewActivityFeed(id)}`);
  },

  createJobDocument(data: ICreateJobDocumentPayload) {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('category', data.category);
    formData.append('fileSize', data.fileSize);
    formData.append('fileType', data.fileType);
    formData.append('fileName', data.fileName);
    formData.append('description', data.description ?? '');
    formData.append('jobId', data.jobId.toString());
    formData.append('visibility', data.visibility);

    return axiosClient.post<IApiBaseResponse<ICreateJobDocumentResponse>>(
      urlApiJobDetails.createJobDocument,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  },

  deleteJobDocument(id: string) {
    return axiosClient.delete<IApiBaseResponse<null>>(
      urlApiJobDetails.deleteJobDocument(id)
    );
  },

  downloadAllJobDocuments(id: string) {
    return axiosClient.get(urlApiJobDetails.downloadAllJobDocuments(id), {
      responseType: 'blob',
    });
  },

  downloadJobDocument(id: string, documentId: string) {
    return axiosClient.get(urlApiJobDetails.downloadJobDocument(id, documentId), {
      responseType: 'blob',
    });
  },

  addNote(id: string, data: { title: string; content: string; isPrivate: boolean; mentionedAccountIds?: number[] }) {
    return axiosClient.post<IApiBaseResponse<any>>(`${urlApiJobDetails.addNote(id)}`, data);
  },

  getMaterialOrders(id: string) {
    return axiosClient.get<IApiBaseResponse<IMaterialOrderResponse[]>>(
      urlApiJobDetails.getMaterialOrders(id)
    );
  },

  searchBranches(stateCode: string) {
    return axiosClient.get<IApiBaseResponse<IBranch[]>>(
      urlApiJobDetails.searchBranches(stateCode)
    );
  },

  searchProducts(branchNumber: string, description: string) {
    return axiosClient.get<IApiBaseResponse<IProduct[]>>(
      urlApiJobDetails.searchProducts(branchNumber, description)
    );
  },

  getProductPricing(payload: IProductPricingPayload) {
    return axiosClient.post<IApiBaseResponse<IProductPricingResponse[]>>(
      urlApiJobDetails.getProductPricing,
      payload
    );
  },

  createMaterialOrder(jobId: string, payload: ICreateMaterialOrderPayload) {
    return axiosClient.post<IApiBaseResponse<any>>(
      urlApiJobDetails.createMaterialOrder(jobId),
      payload
    );
  },

  createMaterialOrderDraft(jobId: string, payload: ICreateMaterialOrderPayload) {
    return axiosClient.post<IApiBaseResponse<any>>(
      urlApiJobDetails.createMaterialOrderDraft(jobId),
      payload
    );
  },

  getMaterialOrderDetail(jobId: string, materialId: string) {
    return axiosClient.get<IApiBaseResponse<IMaterialOrderDetail>>(
      urlApiJobDetails.getMaterialOrder(jobId, materialId)
    );
  },

  updateMaterialOrder(jobId: string, materialId: string, payload: ICreateMaterialOrderPayload) {
    return axiosClient.put<IApiBaseResponse<any>>(
      urlApiJobDetails.updateMaterialOrder(jobId, materialId),
      payload
    );
  },

  updateMaterialOrderDraft(jobId: string, materialId: string, payload: ICreateMaterialOrderPayload) {
    return axiosClient.put<IApiBaseResponse<any>>(
      urlApiJobDetails.updateMaterialOrderDraft(jobId, materialId),
      payload
    );
  },

  getOverviewWorkOrderDetails(id: string) {
    return axiosClient.get<IApiBaseResponse<IOverviewWorkOrderDetails>>(`${urlApiJobDetails.getOverviewWorkOrderDetails(id)}`);
  },

  getOverviewWorkOrderList(params: IParamsGetOverviewWorkOrderList) {
    return axiosClient.get<IApiBaseResponse<IOverviewWorkOrderListResponse>>(`${urlApiJobDetails.getOverviewWorkOrderList}`, { params: params });
  },

  createWorkOrder(data: FormData) {
    return axiosClient.post<IApiBaseResponse<any>>(
      urlApiJobDetails.createWorkOrder,
      data,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  },

  updateWorkOrder(id: string, data: FormData) {
    return axiosClient.put<IApiBaseResponse<any>>(
      urlApiJobDetails.updateWorkOrder(id),
      data,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  },

  deleteWorkOrder(id: string) {
    return axiosClient.delete<IApiBaseResponse<any>>(
      urlApiJobDetails.deleteWorkOrder(id)
    );
  },

  exportWorkOrderPDF(id: string) {
    return axiosClient.get(urlApiJobDetails.exportWorkOrderPDF(id), {
      responseType: 'blob',
    });
  },

  exportWorkOrderPDFFromHTML(html: string, options?: { filename?: string }) {
    return axiosClient.post(
      urlApiJobDetails.exportWorkOrderPDFFromHTML,
      { html, ...options },
      {
        responseType: 'blob',
      }
    );
  },

  jobDetailsAddContact(id: string, data: IPayloadUpdateOverviewCustomerDetails) {
    return axiosClient.post<IApiBaseResponse<any>>(`${urlApiJobDetails.jobDetailsAddContact(id)}`, data);
  },

  getOverviewJobTaskList(id: string, params: IParamsGetOverviewJobTaskList) {
    return axiosClient.get<IApiBaseResponse<IOverviewJobTaskListResponse>>(`${urlApiJobDetails.getOverviewJobTaskList(id)}`, { params: params });
  },

  createJobTask(data: IPayloadCreateJobTask) {
    return axiosClient.post<IApiBaseResponse<any>>(`${urlApiJobDetails.createJobTask}`, data);
  },

  updateJobTask(id: string, data: IPayloadCreateJobTask) {
    return axiosClient.put<IApiBaseResponse<any>>(`${urlApiJobDetails.updateJobTask(id)}`, data);
  },

  deleteJobTask(id: string) {
    return axiosClient.delete<IApiBaseResponse<any>>(`${urlApiJobDetails.updateJobTask(id)}`);
  },

  getInvoiceJobDetailsList(id: string, params: IParamsGetInvoiceJobDetails) {
    return axiosClient.get<IApiBaseResponse<IInvoiceJobDetailsListResponse>>(`${urlApiJobDetails.getInvoiceJobDetailsList(id)}`, { params: params });
  },

  updateStatusInvoiceJobDetails(idJob: string, idInvoice: string, data: { status: string }) {
    return axiosClient.put<IApiBaseResponse<any>>(`${urlApiJobDetails.updateStatusInvoiceJobDetails(idJob, idInvoice)}`, data);
  },

  createInvoice(jobId: string, data: IPayloadCreateInvoice) {
    return axiosClient.post<IApiBaseResponse<any>>(`${urlApiJobDetails.createInvoice(jobId)}`, data);
  },

  // FB-25: edit keeps recorded payments; BE refuses a total below the amount already paid.
  updateInvoice(idJob: string, idInvoice: string, data: IPayloadCreateInvoice) {
    return axiosClient.put<IApiBaseResponse<any>>(`${urlApiJobDetails.getInvoiceDetailsJobDetails(idJob, idInvoice)}`, data);
  },

  // FB-25/27: PDF of the customer-facing invoice.
  downloadInvoicePdf(idJob: string, idInvoice: string) {
    return axiosClient.get(`${urlApiJobDetails.getInvoiceDetailsJobDetails(idJob, idInvoice)}/pdf`, { responseType: 'blob' });
  },

  getListProductInvoice(jobId: string, params: IParamsGetListProductInvoice) {
    return axiosClient.get<IApiBaseResponse<IProductInvoice>>(`${urlApiJobDetails.getListProductInvoice(jobId)}`, {
      params: params,
      timeout: 30000,
    });
  },

  getInvoiceTaxRates(jobId: string) {
    return axiosClient.get<IApiBaseResponse<IInvoiceTaxRate[]>>(urlApiJobDetails.getInvoiceTaxRates(jobId));
  },

  getInvoiceDetailsJobDetails(idJob: string, idInvoice: string) {
    return axiosClient.get<IApiBaseResponse<IInvoiceDetailsJobDetails>>(`${urlApiJobDetails.getInvoiceDetailsJobDetails(idJob, idInvoice)}`);
  },

  // FB-26: admin only (BE 403 otherwise); refused when the invoice has payments.
  deleteInvoice(idJob: string, idInvoice: string) {
    return axiosClient.delete<IApiBaseResponse<any>>(`${urlApiJobDetails.getInvoiceDetailsJobDetails(idJob, idInvoice)}`);
  },

  getListPaymentInFinancialTab(idJob: string) {
    return axiosClient.get<IApiBaseResponse<IPaymentJobDetailsListResponse>>(`${urlApiJobDetails.getListPaymentInFinancialTab(idJob)}`);
  },

  getListBudgetTrackerJobDetails(idJob: string) {
    return axiosClient.get<IApiBaseResponse<IBudgetTrackerJobDetailsListResponse>>(`${urlApiJobDetails.getListBudgetTrackerJobDetails(idJob)}`);
  },

  createStripeCheckout(data: { invoiceId: string; amount: number; returnUrl: string }) {
    return axiosClient.post<IApiBaseResponse<{ checkout_url: string }>>(
      urlApiJobDetails.createStripeCheckout(data.invoiceId),
      {
        return_url: data.returnUrl,
        amount: data.amount,
      }
    );
  },
  getListCreditMemoJobDetails(idJob: string) {
    return axiosClient.get<IApiBaseResponse<ICreditMemoJobDetailsListResponse>>(`${urlApiJobDetails.getListCreditMemoJobDetails(idJob)}`);
  },

  getItemCreateNewBudget(idJob: string, idEstimate: string) {
    return axiosClient.get<IApiBaseResponse<IItemCreateNewBudget[]>>(`${urlApiJobDetails.getItemCreateNewBudget(idJob, idEstimate)}`);
  },

  createNewBudget(idJob: string, data: IPayloadBudgetTrackerJobDetails) {
    return axiosClient.post<IApiBaseResponse<any>>(`${urlApiJobDetails.createNewBudget(idJob)}`, data);
  },

  getDetailsBudgetTrackerJobDetails(idJob: string, idBudget: string) {
    return axiosClient.get<IApiBaseResponse<IDetailsBudgetTrackerJobDetails>>(`${urlApiJobDetails.getDetailsBudgetTrackerJobDetails(idJob, idBudget)}`);
  },

  // "User Created" entry in a budget/invoice Activity list (BE: POST /jobs/:id/finance-activity-logs).
  createFinanceActivityLog(idJob: string, data: { description: string; budgetTracker?: number; invoiceId?: number }) {
    return axiosClient.post<IApiBaseResponse<IFinanceActivityLog>>(`${urlApiJobDetails.createFinanceActivityLog(idJob)}`, data);
  },

  updateDetailsBudgetTrackerJobDetails(idJob: string, idBudget: string, data: IPayloadBudgetTrackerJobDetails) {
    return axiosClient.put<IApiBaseResponse<IDetailsBudgetTrackerJobDetails>>(`${urlApiJobDetails.getDetailsBudgetTrackerJobDetails(idJob, idBudget)}`, data);
  },

  markPaymentAsPaid(idJob: string, idBudget: string, idCommission: string) {
    return axiosClient.put<IApiBaseResponse<IDetailsBudgetTrackerJobDetails>>(`${urlApiJobDetails.markPaymentAsPaid(idJob, idBudget, idCommission)}`);
  },

  createCreditMemo(idJob: string, data: IPayloadCreateCreditMemo) {
    return axiosClient.post<IApiBaseResponse<any>>(`${urlApiJobDetails.createCreditMemo(idJob)}`, data);
  },

  getCreditMemoDetailsJobDetails(idJob: string, idCreditMemo: string) {
    return axiosClient.get<IApiBaseResponse<IDetailsCreditMemoJobDetails>>(`${urlApiJobDetails.getCreditMemoDetailsJobDetails(idJob, idCreditMemo)}`);
  },

  getPublicInvoice(token: string) {
    return axiosClient.get<IApiBaseResponse<IInvoiceInViewInvoice>>(urlApiJobDetails.getPublicInvoice(token));
  },

  createPublicCheckout(token: string) {
    return axiosClient.post<IApiBaseResponse<{ checkout_url: string }>>(urlApiJobDetails.createPublicCheckout(token));
  },

  getInvoiceInViewInvoice(idInvoice: string) {
    return axiosClient.get<IApiBaseResponse<IInvoiceInViewInvoice>>(`${urlApiJobDetails.getInvoiceInViewInvoice(idInvoice)}`);
  },

  emailInvoice(data: IPayloadEmailInvoice) {
    return axiosClient.post<IApiBaseResponse<any>>(`${urlApiJobDetails.emailInvoice}`, data);
  },

  getListInvoiceInLogPaymentModal(idJob: string, params?: { invoiceId?: string }) {
    return axiosClient.get<IApiBaseResponse<IInvoiceInLogPaymentModal[]>>(`${urlApiJobDetails.getListInvoiceInLogPaymentModal(idJob)}`, { params });
  },

  logPayment(idJob: string, data: IPayloadLogPayment) {
    return axiosClient.post<IApiBaseResponse<any>>(`${urlApiJobDetails.logPayment(idJob)}`, data);
  },

  deletePayment(idJob: string, idPayment: string) {
    return axiosClient.delete<IApiBaseResponse<any>>(`${urlApiJobDetails.deletePayment(idJob, idPayment)}`);
  },

  getPaymentDetails(idJob: string, idPayment: string) {
    return axiosClient.get<IApiBaseResponse<IPaymentDetails>>(`${urlApiJobDetails.deletePayment(idJob, idPayment)}`);
  },

  updatePayment(idJob: string, idPayment: string, data: IPayloadLogPayment) {
    return axiosClient.put<IApiBaseResponse<any>>(`${urlApiJobDetails.deletePayment(idJob, idPayment)}`, data);
  },

  createPaymentRequest(idJob: string, data: { amount: number; messageToCustomer?: string }) {
    return axiosClient.post<IApiBaseResponse<any>>(
      urlApiJobDetails.createPaymentRequest(idJob),
      data
    );
  },

  deleteBudgetTracker(idJob: string, idBudget: string) {
    return axiosClient.delete<IApiBaseResponse<any>>(`${urlApiJobDetails.getDetailsBudgetTrackerJobDetails(idJob, idBudget)}`);
  },

  deleteCreditMemo(idJob: string, idCreditMemo: string) {
    return axiosClient.delete<IApiBaseResponse<any>>(`${urlApiJobDetails.getCreditMemoDetailsJobDetails(idJob, idCreditMemo)}`);
  },

  updateCreditMemo(idJob: string, idCreditMemo: string, data: IPayloadCreateCreditMemo) {
    return axiosClient.put<IApiBaseResponse<any>>(`${urlApiJobDetails.getCreditMemoDetailsJobDetails(idJob, idCreditMemo)}`, data);
  },

  toggleArchivedBudgetTracker(idJob: string, idBudget: string) {
    return axiosClient.put<IApiBaseResponse<any>>(`${urlApiJobDetails.toggleArchivedBudgetTracker(idJob, idBudget)}`);
  },
};

export default jobDetailsApi;
